import React, { useEffect, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuizIcon from "@mui/icons-material/Quiz";
import {
  Box,
  Typography,
  Card,
  IconButton,
  Collapse,
  TextField,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";

import SUGGESTED from "../../../assets/icons/suggested.webp";
import { PlanDropDown } from "../../../common";

const QuestionCardViewOnly = ({
  question,
  editable = false,
  onQuestionUpdate,
  onMCQOptionAdd,
  onMCQOptionDelete,
  onMCQOptionUpdate,
  onCorrectAnswerUpdate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [questionText, setQuestionText] = useState(
    question?.question_text || ""
  );
  const [expectedAnswer, setExpectedAnswer] = useState(
    question?.answer_text || ""
  );
  const [mcqOptions, setMcqOptions] = useState(question?.mcqs_options || []);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(
    question?.mcqs?.findIndex(
      (opt) => opt.toLowerCase() === question?.expect_ans?.toLowerCase()
    ) || 0
  );

  // Reset expanded when the question or test changes
  useEffect(() => {
    setExpanded(false);
  }, [question]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleQuestionTextChange = (event) => {
    const newText = event.target.value;
    setQuestionText(newText);
    onQuestionUpdate?.(newText);
  };

  const handleMCQOptionChange = (index, value) => {
    const newOptions = [...mcqOptions];
    newOptions[index] = value;
    setMcqOptions(newOptions);
    onMCQOptionUpdate?.(index, value);
  };

  const handleAddMCQOption = () => {
    const newOption = "";
    setMcqOptions([...mcqOptions, newOption]);
    onMCQOptionAdd?.();
  };

  const handleDeleteMCQOption = (index) => {
    const newOptions = mcqOptions.filter((_, i) => i !== index);
    setMcqOptions(newOptions);
    onMCQOptionDelete?.(index);

    // Adjust correct answer index if needed
    if (index === correctAnswerIndex) {
      setCorrectAnswerIndex(0);
      onCorrectAnswerUpdate?.(0);
    } else if (index < correctAnswerIndex) {
      setCorrectAnswerIndex(correctAnswerIndex - 1);
      onCorrectAnswerUpdate?.(correctAnswerIndex - 1);
    }
  };

  const handleCorrectAnswerChange = (event) => {
    const newIndex = parseInt(event.target.value);
    setCorrectAnswerIndex(newIndex);
    onCorrectAnswerUpdate?.(newIndex);
  };

  const renderMCQEditMode = () => (
    <Box sx={{ width: "100%" }}>
      <Typography sx={{ mb: 1, color: "#4BC7E1", fontWeight: "bold" }}>
        Expected Answer
      </Typography>
      <RadioGroup
        value={correctAnswerIndex}
        onChange={handleCorrectAnswerChange}
      >
        {mcqOptions.map((option, index) => {
          const letter = String.fromCharCode(65 + index);
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                gap: 1,
                position: "relative",
              }}
            >
              <FormControlLabel
                value={index}
                control={
                  <Radio
                    sx={{
                      color: "#4BC7E1",
                      "&.Mui-checked": {
                        color: "#4BC7E1",
                      },
                    }}
                  />
                }
                label={`${letter}:`}
                sx={{ minWidth: 45 }}
              />
              <TextField
                fullWidth
                value={option}
                onChange={(e) => handleMCQOptionChange(index, e.target.value)}
                placeholder={
                  index <= 1
                    ? "Type correct answer here"
                    : "Type other options here"
                }
                variant="outlined"
                size="small"
                sx={{ flex: 1 }}
              />
              <Tooltip title="Remove option">
                <IconButton
                  onClick={() => handleDeleteMCQOption(index)}
                  size="small"
                  sx={{
                    color: "#f44336",
                    visibility: mcqOptions.length <= 2 ? "hidden" : "visible",
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </RadioGroup>
      {mcqOptions.length < 5 && (
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddMCQOption}
          sx={{ mt: 1, color: "text.secondary" }}
        >
          Add option
        </Button>
      )}
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1, color: "text.secondary" }}
      >
        Empty fields will not be visible to candidates
      </Typography>
    </Box>
  );

  // Rest of your component remains the same until the MCQ rendering part
  return (
    <Card
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: "#FBFBFB",
      }}
    >
      {/* Header part remains the same */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        {question?.suggested === "suggested" ? (
          <Box component="img" src={SUGGESTED} width={100} height={25} />
        ) : (
          <Box
            sx={{
              display: "inline-block",
              py: 0.5,
              borderRadius: 5,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "black",
                fontWeight: 500,
              }}
            >
              Custom Question
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <QuizIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" sx={{ mr: 2 }}>
            {question?.question_category === "multiple choice"
              ? "multiple choice"
              : "text"}
          </Typography>
          <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
          {editable ? (
            <PlanDropDown
              data={[
                "1 minute",
                "2 minute",
                "3 minute",
                "4 minute",
                "5 minute",
                "6 minute",
                "7 minute",
                "8 minute",
              ]}
            />
          ) : (
            <Typography variant="body2">{question?.time_to_answer}</Typography>
          )}

          <IconButton
            onClick={handleExpandClick}
            sx={{
              marginLeft: "auto",
              transition: "transform 0.3s",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "inline-block",
          bgcolor: "#4BC7E1",
          px: 1.5,
          py: 0.5,
          borderRadius: 5,
          my: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "white",
          }}
        >
          Question
        </Typography>
      </Box>

      {editable && expanded ? (
        <TextField
          fullWidth
          multiline
          rows={2}
          value={questionText}
          onChange={handleQuestionTextChange}
          variant="outlined"
          sx={{ mb: 2 }}
          placeholder="Type your question here"
        />
      ) : (
        <Typography variant="body1" sx={{ mb: 1 }}>
          {question?.question_text}
        </Typography>
      )}

      <Box
        sx={{
          display: "inline-block",
          bgcolor: "#4BC7E1",
          px: 1.5,
          py: 0.5,
          borderRadius: 5,
          mt: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "white",
          }}
        >
          Expected Answer
        </Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ my: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
          {question?.question_category === "multiple choice" ? (
            editable ? (
              renderMCQEditMode()
            ) : (
              <>
                {question?.mcqs_options?.map((option, index) => {
                  const letter = String.fromCharCode(65 + index);
                  const isCorrect =
                    question?.answer_text.toLowerCase() ===
                    option?.toLowerCase();

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        position: "relative",
                        cursor: "pointer",
                        p: 1,
                        ml: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          bgcolor: isCorrect ? "#4caf50" : "#f44336",
                          borderRadius: "3px",
                          position: "absolute",
                          left: -24,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ mr: 1, fontWeight: 500 }}>
                          {`${letter}:`}
                        </Typography>
                        <Typography>{option}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )
          ) : editable ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={expectedAnswer}
              onChange={(e) => setExpectedAnswer(e.target.value)}
              variant="outlined"
              placeholder="Type expected answer here"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {question?.answer_text}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

export default QuestionCardViewOnly;
